/**
 * Where to bias a geocode lookup toward — derived from the call, not hardcoded.
 *
 * ## Why this exists
 *
 * A bare or mistyped address is ambiguous worldwide. Measured live on staging,
 * `145 Industrial Park Drive, Etobicoke` resolves to **Grayville, Illinois —
 * 918 km away, in another country** — because nothing tells the geocoder where
 * on Earth to look. Supplying a nearby anchor pulled the same query to 110 km,
 * and the correct locality resolves at 15 km.
 *
 * The first version of this used a single configured Toronto point. That was
 * wrong for two reasons:
 *   1. The platform is multi-tenant and the map already ships an `eu` profile
 *      centred on Prague, so a global Toronto default biases a European call
 *      toward Ontario — the original wrong-continent defect, inverted.
 *   2. A tenant centroid is a poor anchor even in the right city. The incident
 *      is near the PEOPLE ON THE CALL, and we usually know where they are.
 *
 * So the anchor is resolved per call, most-specific first, and the configured
 * point is only the last resort.
 *
 * ## Precedence
 *
 *   1. Caller GPS — the citizen consented to share it at pre-join. The incident
 *      is almost always near them, so this is the strongest signal available.
 *   2. Officer GPS — a responding officer is typically in the right area.
 *   3. An already-geocoded location on this call that is not itself approximate —
 *      weaker (it is a geocode result) but still call-local evidence.
 *      ⚠ NOT "coherent", which this claimed until 2026-08-19: there is no
 *      coherence, role or corroboration check. The first eligible entry in
 *      entity order wins.
 *   4. The serving region of the caller's area code. Coarse, and derived from a
 *      SPOKEN number rather than the connection, so it sits below anything
 *      this call actually established. See the note at the branch itself.
 *   5. The configured deployment default.
 *
 * ## Non-negotiables
 *
 * ⛔ An anchor is a BIAS, never a filter. It changes which candidate ranks
 *    first; it must never cause a result to be discarded. A caller reporting
 *    something 300 km away must still geocode — that lesson cost a revert
 *    already (see app/api/geocode/route.ts).
 *
 * ⛔ Never trust a fix without sanity-checking it. A GPS payload arrives from a
 *    browser over a data channel and can be absent, zeroed, or wildly stale. A
 *    bad anchor is worse than none: it biases confidently toward the wrong
 *    place, which is the exact failure mode being fixed.
 */

import { areaCodeFromPhone } from './addressContext';
import { anchorForAreaCode } from './areaCodeAnchor';

/**
 * Coordinate sanity for a non-GPS anchor.
 *
 * `isUsableFix` is deliberately NOT reused: its `MAX_FIX_ACCURACY_M` cap (50 km) is a
 * statement about GPS quality, and every area-code anchor is coarser than that by
 * design. Running one through it would reject all of them — a guard rejecting the thing
 * it was never written to judge.
 */
function _isValidCoordinate(lat: number, lon: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false;
  return !(Math.abs(lat) < 0.001 && Math.abs(lon) < 0.001);
}

export interface GeoFix {
  latitude: number;
  longitude: number;
  /** Metres of uncertainty, if the source reports it. */
  accuracy?: number | null;
  /** Epoch ms when the fix was taken, if known. */
  capturedAt?: number | null;
}

export type AnchorSource =
  | 'caller_gps'
  | 'officer_gps'
  | 'caller_area_code'
  | 'call_location'
  | 'configured_default';

export interface GeocodeAnchor {
  lat: number;
  lon: number;
  source: AnchorSource;
  /** Metres, when the winning source reported it. Diagnostics only. */
  accuracy?: number | null;
}

export interface AnchorInputs {
  callerGps?: GeoFix | null;
  officerGps?: GeoFix | null;
  /**
   * The caller's phone number as the analysis layer holds it. It may come from
   * something said on the call, from `[SYSTEM] Caller information` intake metadata
   * (which `entity_v2/people.py` admits as "claimed caller identity and contact
   * fields"), or be assigned directly from submitted form data by
   * `entity_orchestrator.py`. None of those is the SIP ANI and none is attested.
   * Its NANP area code yields a regional anchor of last resort.
   *
   * ⚠ It used to be described here as "the one location signal almost always
   * present". Presence is not the claim that matters: a number is nearly always
   * available, and is nearly always a weak statement about WHERE SOMEONE IS.
   */
  callerPhone?: string | null;
  /**
   * Already-geocoded locations from this call.
   *
   * ⚠ NOT "most trustworthy first", which this said until 2026-08-19. The producer
   * (`aiAnalysisThunks.ts`) filters on `geocodingStatus === 'success'` and otherwise
   * preserves ENTITY-ARRAY ORDER. The backend does apply its own provider
   * thresholds, but "success" carries no ROLE and no ordering guarantee: a
   * suspect's destination extracted before the incident scene sorts first just
   * as easily.
   *
   * `precision` carries the backend's own stamp so a rescued fuzzy match can be
   * refused below; pass it through rather than dropping it.
   */
  callLocations?: Array<{
    latitude?: number | null;
    longitude?: number | null;
    precision?: string | null;
    /**
     * The backend's `approximate` flag, forwarded. TRUE for a result that is
     * NOT the address that was asked for: a fuzzy match rescued by proximity,
     * or a locality centroid substituted when a street lookup failed. This is
     * the field the anchor-eligibility rule turns on — `precision` is display
     * granularity and is OVERWRITTEN to "locality" by the fallback path, which
     * erases the `fuzzy_match` stamp.
     */
    approximate?: boolean | null;
  }> | null;
  /** Deployment fallback — the map profile centre, or the configured default. */
  fallback?: { lat: number; lon: number } | null;
  /** Clock injection for tests. */
  now?: number;
}

/**
 * A fix older than this is ignored.
 *
 * Six hours is deliberately generous: a fix from the start of a long call still
 * says which CITY we are in, which is all a bias needs. The purpose of the bound
 * is to reject a stale fix from a previous session or a clock-skewed payload, not
 * to demand freshness.
 */
export const MAX_FIX_AGE_MS = 6 * 60 * 60 * 1000;

/**
 * A fix less precise than this is ignored.
 *
 * 50 km — again deliberately loose. A ±20 km cell-tower fix still pulls a query
 * from the wrong continent to the right metro area, which is the entire point.
 * Rejecting coarse fixes would throw away most of the value; the bound exists to
 * catch nonsense values (e.g. accuracy reported in the millions).
 */
export const MAX_FIX_ACCURACY_M = 50_000;

/** Null Island and its neighbours — the classic "no fix" sentinel. */
function isNullIsland(lat: number, lon: number): boolean {
  return Math.abs(lat) < 0.001 && Math.abs(lon) < 0.001;
}

/**
 * Is this fix usable as a bias anchor?
 *
 * Exported because "why was my GPS ignored?" is a question worth being able to
 * answer directly in a test or a log line.
 */
export function isUsableFix(fix: GeoFix | null | undefined, now?: number): boolean {
  if (!fix) return false;
  const { latitude: lat, longitude: lon } = fix;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false;
  // (0,0) is in the Gulf of Guinea. Real calls are not, and a zeroed payload is
  // the commonest way a "present" fix is actually absent.
  if (isNullIsland(lat, lon)) return false;

  if (fix.accuracy != null) {
    if (!Number.isFinite(fix.accuracy)) return false;
    // A negative or absurd accuracy means the producer is broken; don't guess.
    if (fix.accuracy < 0 || fix.accuracy > MAX_FIX_ACCURACY_M) return false;
  }

  if (fix.capturedAt != null) {
    if (!Number.isFinite(fix.capturedAt)) return false;
    const reference = now ?? Date.now();
    const age = reference - fix.capturedAt;
    // Reject stale fixes, and reject fixes from the future by more than a small
    // clock-skew allowance — a far-future stamp means the clock or the units are
    // wrong, and an age computed from it is meaningless.
    if (age > MAX_FIX_AGE_MS) return false;
    if (age < -5 * 60 * 1000) return false;
  }

  return true;
}

/**
 * Resolve the anchor to bias a geocode toward.
 *
 * Returns null when nothing usable is available — callers must treat that as
 * "send no proximity", never as "use some default point", because inventing an
 * anchor is how a European call gets biased to Ontario.
 */
export function resolveGeocodeAnchor(inputs: AnchorInputs): GeocodeAnchor | null {
  const now = inputs.now ?? Date.now();

  if (isUsableFix(inputs.callerGps, now)) {
    const f = inputs.callerGps as GeoFix;
    return {
      lat: f.latitude,
      lon: f.longitude,
      source: 'caller_gps',
      accuracy: f.accuracy ?? null,
    };
  }

  if (isUsableFix(inputs.officerGps, now)) {
    const f = inputs.officerGps as GeoFix;
    return {
      lat: f.latitude,
      lon: f.longitude,
      source: 'officer_gps',
      accuracy: f.accuracy ?? null,
    };
  }

  // A previously geocoded location on this call. Weaker than a device fix — it
  // is itself a geocode result and could be the very bad pin we are trying to
  // avoid repeating — but it is still call-local and beats a tenant centroid.
  for (const loc of inputs.callLocations || []) {
    // ⛔ AN APPROXIMATE RESULT MUST NEVER BECOME THE NEXT ANCHOR.
    // `geocoding.py` sets `approximate` on two kinds of result, and neither may
    // anchor a later lookup:
    //
    //   - a fuzzy match RESCUED because it was within 250 km of the anchor in
    //     force at the time (`_stamp_fuzzy_match`). Letting it anchor the next
    //     lookup closes the loop the anchor exists to avoid: the pin proves
    //     itself, and each round drifts with no external correction.
    //   - a LOCALITY CENTROID substituted when the street address failed. It is
    //     a city-centre guess standing in for an address nobody resolved.
    //
    // ⚠ TEST `approximate`, NOT `precision`. The locality-fallback path sets
    // `result["precision"] = "locality"` AFTER the fuzzy stamp, overwriting
    // `fuzzy_match` — so a precision-only check silently passes exactly the
    // rescued result it was written to refuse. `approximate` survives both.
    // `precision` is still checked as defence in depth.
    // Same provenance-ineligibility rule `aiAnalysisThunks.ts` already applies
    // to provider-inferred jurisdiction, for the same reason.
    if (loc?.approximate === true) continue;
    if (loc?.precision === 'fuzzy_match') continue;

    const candidate: GeoFix = {
      latitude: loc?.latitude as number,
      longitude: loc?.longitude as number,
    };
    if (isUsableFix(candidate, now)) {
      return {
        lat: candidate.latitude,
        lon: candidate.longitude,
        source: 'call_location',
        accuracy: null,
      };
    }
  }

  // Phone-area-code fallback. `callerPhone` reaches here from
  // `aiAnalysis.entities.caller?.phone`, which is populated by entity extraction from
  // speech, by `[SYSTEM] Caller information` intake metadata, or directly from
  // submitted form data (`entity_orchestrator.py`). It is NOT the SIP ANI and it is
  // NOT proof of where the caller is now: the number may be
  // ported, nomadic VoIP, a relay centre's callback, someone else's, or simply
  // misattributed by extraction. So its coarse serving-region point is consulted only
  // when no usable GPS and no already-geocoded call location exist, and only ahead of
  // the configured default. It biases provider ranking and the 250 km proximity rescue
  // in `api-ai-engine/app/core/tools/geocoding.py`; it does not establish incident
  // location.
  //
  // ⚠ THIS USED TO RANK ABOVE `call_location`, justified by "derived from the caller's
  // own number, so it CANNOT be poisoned". That premise was false, and measured: a
  // relayed call and an ordinary ported mobile both anchored 352 km from the caller
  // (`anchor.relayMeasurement.test.ts`). The tradeoff is NOT free and is recorded here
  // rather than erased — `call_location` really can be a bad pin feeding back on
  // itself (the Grayville case named in `geocoding.py`), and that persists through the
  // moderate-score rescue too, not only through above-threshold results. It is ranked
  // higher anyway because a non-local number needs no special circumstance at all,
  // while the loop needs a prior WRONG geocode to have been stored. Porting does not
  // by itself imply relocation — but relay, nomadic VoIP, a work or family number,
  // and plain misattribution all land in the same place.
  //
  // The loop is not left armed: a rescued `fuzzy_match` is refused as an anchor
  // above, which is the specific mechanism by which a bad pin re-anchored itself.
  //
  // ⚠ Deliberately NOT the province from `regionFromPhone`: Ontario's centroid sits
  // ~500 km north of Toronto, so a province anchor would drag most Ontario calls away
  // from the caller. `areaCodeAnchor.ts` maps to SERVING REGIONS instead, and omits
  // codes (867, 250, 819, 873) whose territory is too large for any point to mean
  // anything. (807 is NOT omitted — it is in the table with a 300 km accuracy; this
  // list said otherwise until 2026-08-19.)
  const areaAnchor = anchorForAreaCode(areaCodeFromPhone(inputs.callerPhone));
  if (areaAnchor && _isValidCoordinate(areaAnchor.lat, areaAnchor.lon)) {
    return {
      lat: areaAnchor.lat,
      lon: areaAnchor.lon,
      source: 'caller_area_code',
      accuracy: areaAnchor.accuracyM,
    };
  }

  const fb = inputs.fallback;
  if (
    fb &&
    Number.isFinite(fb.lat) &&
    Number.isFinite(fb.lon) &&
    !isNullIsland(fb.lat, fb.lon)
  ) {
    return { lat: fb.lat, lon: fb.lon, source: 'configured_default', accuracy: null };
  }

  return null;
}
